import * as React from 'react';
import { DatePicker } from 'Src/components/DatePicker/DatePicker';
import { SchoolEvent } from 'Src/models/event';
import { Tournament, TournamentStructureMatch } from 'Src/models/tournament';
import { AppUser } from 'Src/views/App/App';
import { getAllTournamentEvents, updateTournamentEventsBulk } from 'Src/helpers/service/adminSU/tournamentEventsTable';
import './TournamentScheduleGrid.scss';
import { tournamentFixtureDisplay } from 'Src/helpers/tournament/tournamentFixtureDisplay';

export interface SchedulePosition {
  startTime: string;
  endTime: string;
  pitchId: string | null;
}

interface Fixture {
  key: string;
  name: string;
  groupName?: string;
  startTime?: string;
  endTime?: string;
  pitchId?: string | null;
  teams: string[];
  eventId?: string;
  matchId?: string;
  locked?: boolean;
}

interface Props {
  user: AppUser;
  tournament: Tournament;
  matches: TournamentStructureMatch[];
  eventByMatch: Map<string, string | undefined>;
  matchLabel: (match: TournamentStructureMatch) => string;
  defaultDuration: (match: TournamentStructureMatch) => number;
  onSaveDraft: (positions: Record<string, SchedulePosition>) => Promise<boolean>;
  onEventsSaved: (positions: Record<string, SchedulePosition>) => void;
  onDirty: (dirty: boolean) => void;
  onSaving: (saving: boolean) => void;
}

const minute = 60000;
const step = 5;
const twoDigits = (value: number) => `0${value}`.slice(-2);
const dateKey = (date: Date) =>
  [date.getFullYear(), twoDigits(date.getMonth() + 1), twoDigits(date.getDate())].join('-');
const timeLabel = (date: Date) => date.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' });
const timestamp = (value?: string) => Date.parse(value || '');
const lockedEvent = (event: SchoolEvent) => ['FINISHED', 'CANCELED'].includes(event.status);

export const TournamentScheduleGrid: React.FC<Props> = ({
  user,
  tournament,
  matches,
  eventByMatch,
  matchLabel,
  defaultDuration,
  onSaveDraft,
  onEventsSaved,
  onDirty,
  onSaving
}) => {
  const [events, setEvents] = React.useState<SchoolEvent[]>();
  const [loadError, setLoadError] = React.useState(false);
  const [reload, setReload] = React.useState(0);
  const [day, setDay] = React.useState(() => dateKey(new Date(tournament.startTime)));
  const [selected, setSelected] = React.useState<string>();
  const [positions, setPositions] = React.useState<Record<string, SchedulePosition>>({});
  const [saving, setSaving] = React.useState(false);
  const [message, setMessage] = React.useState('');
  const hasChanges = Object.keys(positions).length > 0;

  React.useEffect(() => {
    let cancelled = false;
    setLoadError(false);
    getAllTournamentEvents(user, tournament.id)
      .then((value: SchoolEvent[]) => {
        if (!cancelled) setEvents(value);
      })
      .catch(() => {
        if (!cancelled) setLoadError(true);
      });
    return () => {
      cancelled = true;
    };
  }, [user.roleSessionKey, user.activeSchoolId, tournament.id, reload]);
  React.useEffect(() => {
    onDirty(hasChanges);
  }, [onDirty, hasChanges]);

  const eventIds = new Set(events?.map(event => event.id));
  const matchesByEvent = new Map<string, string>();
  matches.forEach(match => {
    const eventId = eventByMatch.get(match.id || '') || match.eventId;
    if (eventId && match.id) matchesByEvent.set(eventId, match.id);
  });
  const groupNames = new Map([
    ...(tournament.competitionStructure?.draft?.layout?.groups || []),
    ...(tournament.customGroups || []),
    ...(tournament.competitionStructure?.published?.layout?.groups || [])
  ].map(group => [group.id, group.name]));
  const groupBadge = (groupId: string, label?: string) => {
    const name = groupNames.get(groupId);
    return name && !label?.includes(name) ? name : undefined;
  };
  const linkedIds = new Set(events?.map(event => event.tournamentStructureMatchId || matchesByEvent.get(event.id)));
  const fixtures: Fixture[] = [
    ...(events || [])
      .filter(event => event.status !== 'CANCELED')
      .map(event => {
        const matchId = event.tournamentStructureMatchId || matchesByEvent.get(event.id);
        const { match: publishedMatch } = tournamentFixtureDisplay(tournament, event);
        const label = matches.find(match => match.id === matchId)?.name || publishedMatch?.name;
        const matchup = (event.embeddedTeams || [])
          .map((team, index) => {
            const school = tournament.schools?.find(item => item.id === team.schoolId);
            const origin = index === 0 ? publishedMatch?.homeOriginLabel : publishedMatch?.awayOriginLabel;
            return [school?.name, team.name !== school?.name && team.name, origin].filter(Boolean).join(' · ');
          })
          .filter(Boolean)
          .join(' v ');
        return {
          key: `event-${event.id}`,
          eventId: event.id,
          matchId,
          name: [matchup, label].filter(Boolean).join(' · ') || 'Tournament fixture',
          groupName: groupBadge(event.tournamentGroupId, label),
          teams: (event.embeddedTeams || []).map(team => team.cloneOf || team._id).filter(Boolean),
          startTime: event.startTime,
          endTime: event.endTime,
          pitchId: event.pitchId,
          locked: lockedEvent(event)
        };
      }),
    ...matches
      .filter(
        match =>
          match.id &&
          !match.removed &&
          !linkedIds.has(match.id) &&
          !eventIds.has(eventByMatch.get(match.id) || match.eventId || '')
      )
      .map(match => ({
        key: `match-${match.id}`,
        matchId: match.id,
        name: [matchLabel(match), match.name].filter(Boolean).join(' · '),
        groupName: groupBadge(match.groupId, match.name),
        teams: [match.homeSource.teamId, match.awaySource.teamId].filter(Boolean) as string[],
        startTime: match.startTime,
        endTime:
          match.endTime ||
          (Number.isFinite(timestamp(match.startTime))
            ? new Date(timestamp(match.startTime) + defaultDuration(match) * minute).toISOString()
            : undefined),
        pitchId: match.pitchId
      }))
  ];
  const current = fixtures.map(fixture => ({ ...fixture, ...positions[fixture.key] }));
  const canMove = (fixture: Fixture) =>
    !fixture.locked && ((!fixture.eventId && !fixture.startTime) || timestamp(fixture.startTime) > Date.now());
  const midnight = new Date(`${day}T00:00:00`);
  const endOfDay = new Date(midnight);
  endOfDay.setDate(endOfDay.getDate() + 1);
  const minutesInDay = (+endOfDay - +midnight) / minute;
  const timeOnDay = (value: number) => new Date(+midnight + value * minute);
  const dayFixtures = current.filter(
    fixture => timestamp(fixture.startTime) < +endOfDay && timestamp(fixture.endTime) > +midnight
  );
  const tournamentStart = timestamp(tournament.startTime);
  const tournamentEnd = timestamp(tournament.endTime);
  const bound = (time: number) => Math.max(0, Math.min(minutesInDay, (time - +midnight) / minute));
  const from =
    Math.floor(
      Math.min(
        dateKey(new Date(tournamentStart)) === day ? bound(tournamentStart) : 9 * 60,
        ...dayFixtures.map(fixture => bound(timestamp(fixture.startTime)))
      ) / step
    ) * step;
  const to = Math.min(
    minutesInDay,
    Math.ceil(
      Math.max(
        from + 60,
        dateKey(new Date(tournamentEnd)) === day ? bound(tournamentEnd) : 17 * 60,
        ...dayFixtures.map(fixture => bound(timestamp(fixture.endTime)))
      ) / step
    ) * step
  );
  const pitches = [
    ...(tournament.pitches || []).map(pitch => ({ id: pitch.id as string | null, name: pitch.name })),
    { id: null, name: 'No pitch' }
  ];
  const slots = Array.from({ length: Math.max(0, (to - from) / step) }, (_, index) => from + index * step);
  const slotHeight = 20;

  const clash = (fixture: Fixture, target: SchedulePosition, schedule: Fixture[]) =>
    schedule.find(
      other =>
        other.key !== fixture.key &&
        timestamp(other.startTime) < timestamp(target.endTime) &&
        timestamp(other.endTime) > timestamp(target.startTime) &&
        ((target.pitchId && target.pitchId === other.pitchId) || other.teams.some(team => fixture.teams.includes(team)))
    );

  const move = (key: string, start: Date, pitchId: string | null) => {
    const fixture = fixtures.find(item => item.key === key);
    if (!fixture || !canMove(fixture) || saving) return;
    const match = matches.find(item => item.id === fixture.matchId);
    const originalDuration = timestamp(fixture.endTime) - timestamp(fixture.startTime);
    const duration = originalDuration > 0 ? originalDuration : (match ? defaultDuration(match) : 20) * minute;
    if (+start <= Date.now() || +start + duration > +endOfDay) {
      setMessage('Choose a future time with enough room before midnight.');
      return;
    }
    const target = { startTime: start.toISOString(), endTime: new Date(+start + duration).toISOString(), pitchId };
    const conflict = clash(fixture, target, current);
    if (conflict) {
      setMessage(`That time overlaps with ${conflict.name} on the same pitch or for the same team.`);
      return;
    }
    setPositions(previous => {
      const next = { ...previous };
      if (+start === timestamp(fixture.startTime) && (fixture.pitchId || null) === pitchId) delete next[key];
      else next[key] = target;
      return next;
    });
    setSelected(undefined);
    setMessage('');
  };

  const save = async () => {
    if (saving || !hasChanges) return;
    const changed = fixtures.filter(fixture => positions[fixture.key]);
    if (
      changed.length !== Object.keys(positions).length ||
      changed.some(
        fixture =>
          !canMove(fixture) ||
          timestamp(positions[fixture.key].startTime) <= Date.now() ||
          clash(fixture, positions[fixture.key], current)
      )
    ) {
      setMessage('The schedule has changed. Review the pending moves before saving.');
      return;
    }
    const live = changed.filter(fixture => fixture.eventId);
    const planned = changed.filter(fixture => !fixture.eventId && fixture.matchId);
    const byMatch = (items: Fixture[]) =>
      items.reduce<Record<string, SchedulePosition>>((result, item) => {
        if (item.matchId) result[item.matchId] = positions[item.key];
        return result;
      }, {});
    const clear = (items: Fixture[]) =>
      setPositions(previous => {
        const next = { ...previous };
        items.forEach(item => delete next[item.key]);
        return next;
      });
    setSaving(true);
    onSaving(true);
    setMessage('');
    let liveSaved = false;
    try {
      if (live.length) {
        await updateTournamentEventsBulk(
          user,
          tournament.id,
          live.map(fixture => ({ eventId: fixture.eventId, ...positions[fixture.key] })),
          true
        );
        liveSaved = true;
        const byEvent = new Map(live.map(fixture => [fixture.eventId, positions[fixture.key]]));
        setEvents(previous =>
          previous?.map(event => {
            const position = byEvent.get(event.id);
            return position ? { ...event, ...position, pitchId: position.pitchId || undefined } : event;
          })
        );
        clear(live);
      }
      if (planned.length) {
        if (!(await onSaveDraft(byMatch([...live, ...planned])))) {
          setMessage(
            live.length
              ? 'Existing fixtures were updated. The draft moves still need to be saved.'
              : 'The draft moves could not be saved.'
          );
          return;
        }
        clear(planned);
      }
      setMessage(planned.length ? 'Schedule saved. Publish the structure to apply draft fixtures.' : 'Schedule saved.');
    } catch (error) {
      setMessage(
        error?.response?.data?.details?.text || 'The schedule could not be saved. Your moves are still pending.'
      );
    } finally {
      if (liveSaved) onEventsSaved(byMatch(live));
      setSaving(false);
      onSaving(false);
    }
  };

  const fixtureButton = (fixture: Fixture, style?: React.CSSProperties) => (
    <button
      key={fixture.key}
      type="button"
      className="tswScheduleFixture"
      style={style}
      draggable={!saving && canMove(fixture)}
      disabled={saving || !canMove(fixture)}
      aria-pressed={selected === fixture.key}
      title={[fixture.groupName, fixture.name].filter(Boolean).join(' · ')}
      onClick={() => setSelected(selected === fixture.key ? undefined : fixture.key)}
      onDragStart={event => {
        event.dataTransfer.setData('application/x-tournament-fixture', fixture.key);
        event.dataTransfer.effectAllowed = 'move';
      }}
    >
      {fixture.startTime && <strong>{timeLabel(new Date(fixture.startTime))} </strong>}
      {fixture.groupName && <span className="tswScheduleGroup">{fixture.groupName}</span>}
      {fixture.name}
    </button>
  );

  return (
    <section className="tswSchedule" aria-label="Tournament schedule">
      <header>
        <div>
          <h2>Tournament schedule</h2>
          <p>Drag a fixture, or select it and choose a time and pitch. Changes apply when you save.</p>
        </div>
        <div className="tswScheduleControls">
        <DatePicker label="Date" value={new Date(`${day}T00:00:00`)} onChange={value => {
          if (value instanceof Date) setDay(dateKey(value));
        }} clearable={false} size="sm" />
        <button className="tswScheduleAction"
          type="button"
          disabled={saving || !hasChanges}
          onClick={() => {
            setPositions({});
            setSelected(undefined);
            setMessage('');
          }}
        >
          Discard moves
        </button>
        <button
          type="button"
          className="tswScheduleAction is-primary"
          disabled={saving || !hasChanges || loadError}
          onClick={() => void save()}
        >
          {saving ? 'Saving…' : 'Save schedule'}
        </button>
        </div>
      </header>
      {message && <p role="status">{message}</p>}
      {loadError ? (
        <p role="alert">
          Fixtures could not be loaded.{' '}
          <button type="button" onClick={() => setReload(value => value + 1)}>
            Retry
          </button>
        </p>
      ) : !events ? (
        <p role="status">Loading fixtures…</p>
      ) : (
        <>
          {current.some(fixture => !fixture.startTime) && (
            <div className="tswScheduleUnscheduled">
              <strong>Unscheduled</strong>
              {current.filter(fixture => !fixture.startTime).map(fixture => fixtureButton(fixture))}
            </div>
          )}
          <div className="tswScheduleScroll">
            <div
              className="tswScheduleGrid"
              style={{ gridTemplateColumns: `64px repeat(${pitches.length}, minmax(155px, 1fr))` }}
            >
              <div className="tswScheduleHeading">Time</div>
              {pitches.map(pitch => (
                <div className="tswScheduleHeading" key={pitch.id || 'unassigned'}>
                  {pitch.name}
                </div>
              ))}
              <div>
                {slots.map((value, index) => (
                  <div className="tswScheduleTime" key={value} style={{ height: slotHeight }}>
                    {index % 3 === 0 ? timeLabel(timeOnDay(value)) : ''}
                  </div>
                ))}
              </div>
              {pitches.map(pitch => (
                <div className="tswScheduleColumn" key={pitch.id || 'unassigned'}>
                  {slots.map((value, index) => {
                    const start = timeOnDay(value);
                    return (
                      <button
                        type="button"
                        key={value}
                        className={`tswScheduleSlot${index % 3 === 0 ? ' is-quarter' : ''}`}
                        style={{ height: slotHeight }}
                        tabIndex={selected ? 0 : -1}
                        aria-label={`${pitch.name}, ${timeLabel(start)}`}
                        onClick={() => selected && move(selected, start, pitch.id)}
                        onDragOver={event => {
                          if (!saving && event.dataTransfer.types.includes('application/x-tournament-fixture'))
                            event.preventDefault();
                        }}
                        onDrop={event => {
                          event.preventDefault();
                          move(event.dataTransfer.getData('application/x-tournament-fixture'), start, pitch.id);
                        }}
                      />
                    );
                  })}
                  {dayFixtures
                    .filter(fixture => (fixture.pitchId || null) === pitch.id)
                    .map(fixture =>
                      fixtureButton(fixture, {
                        top: ((bound(timestamp(fixture.startTime)) - from) / step) * slotHeight,
                        height: Math.max(
                          slotHeight,
                          ((bound(timestamp(fixture.endTime)) - bound(timestamp(fixture.startTime))) / step) *
                            slotHeight
                        )
                      })
                    )}
                </div>
              ))}
            </div>
          </div>
        </>
      )}
    </section>
  );
};
